import { api, toQuery } from "./client";
import type { ProfileCommandListResponse, ProfileDeletePreview, ProfileDetailResponse, ProfileListResponse, ProfileTemplate } from "../lib/types";

export function listProfiles(params: {
  page: number;
  per_page: number;
  keyword?: string;
  status?: string;
  anomaly?: string;
  manager_id?: string;
  maintenance?: string;
  sort_by?: "last_started_at";
  sort_order?: "asc" | "desc";
}) {
  return api<ProfileListResponse>(`/api/v1/browser-profiles${toQuery(params)}`);
}

export function getProfile(id: string) {
  return api<ProfileDetailResponse>(`/api/v1/browser-profiles/${encodeURIComponent(id)}`);
}

export function listProfileCommandLogs(params: {
  page: number;
  per_page: number;
  action?: string;
  status?: string;
}) {
  return api<ProfileCommandListResponse>(`/api/v1/profile-commands${toQuery(params)}`);
}

export function setProfileMaintenance(id: string, enabled: boolean) {
  return api<{ profile: ProfileDetailResponse["profile"] }>(`/api/v1/browser-profiles/${encodeURIComponent(id)}/maintenance`, {
    method: "POST",
    body: JSON.stringify({ enabled })
  });
}

export function runProfileAction(id: string, action: string, params: Record<string, unknown> = {}) {
  return api<{ command: unknown }>(`/api/v1/browser-profiles/${encodeURIComponent(id)}/actions`, {
    method: "POST",
    body: JSON.stringify({ action, params })
  });
}

export function updateProfileConfig(id: string, config: Record<string, unknown>) {
  return api<{ command: unknown }>(`/api/v1/browser-profiles/${encodeURIComponent(id)}/config`, {
    method: "PATCH",
    body: JSON.stringify({ config })
  });
}

export function previewProfileDelete(profileIds: string[]) {
  return api<ProfileDeletePreview>("/api/v1/browser-profiles/delete-preview", {
    method: "POST",
    body: JSON.stringify({ profile_ids: profileIds })
  });
}

export function batchDeleteProfiles(profileIds: string[]) {
  return api<{ message: string; preview: ProfileDeletePreview }>("/api/v1/browser-profiles/batch-delete", {
    method: "POST",
    body: JSON.stringify({ profile_ids: profileIds })
  });
}

export function retryProfileCommand(commandId: string) {
  return api<{ message: string }>(`/api/v1/profile-commands/${encodeURIComponent(commandId)}/retry`, { method: "POST" });
}

export function listProfileTemplates() {
  return api<{ templates: ProfileTemplate[] }>("/api/v1/profile-templates");
}

export function createProfileTemplate(payload: { name: string; description: string; config: Record<string, unknown>; change_note: string }) {
  return api<{ template: ProfileTemplate }>("/api/v1/profile-templates", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function addProfileTemplateVersion(templateId: string, payload: { config: Record<string, unknown>; change_note: string }) {
  return api<{ version: ProfileTemplate["versions"][number] }>(`/api/v1/profile-templates/${encodeURIComponent(templateId)}/versions`, {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function runProfileBatchAction(profileIds: string[], action: string, options: { template_id?: string; template_version?: number } = {}) {
  return api<{ message: string; accepted: number; total: number; results: Array<{ id: string; ok: boolean; error?: string }> }>("/api/v1/browser-profiles/batch-actions", {
    method: "POST",
    body: JSON.stringify({ profile_ids: profileIds, action, ...options })
  });
}

export function runBrowserManagerAction(managerId: string, action: "sync_inventory" | "sync_disk_usage") {
  return api<{ message: string }>(`/api/v1/browser-managers/${encodeURIComponent(managerId)}/actions`, {
    method: "POST",
    body: JSON.stringify({ action })
  });
}
